import { Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Finding, WorkerFleet } from '../../api/health';
import { QueueState, formatCount, pipelineTitle } from '../../api/queueMonitor';
import { AppFrame } from './AppFrame';
import { BuildLine } from './BuildLine';
import { FunctionChip } from './FunctionChip';
import { QueueStep } from './QueueStep';
import {
    BoardLane,
    BoardNode,
    BoardRow,
    appFindings,
    buildBoard,
    queueFindings,
} from './boardModel';
import './QueueBoard.scss';

export interface IQueueBoardProps {
    queues: QueueState[];
    fleet?: WorkerFleet | null;
    findings: Finding[];
    selectedQueue: string | null;
    onSelectQueue: (name: string) => void;
    openWorker?: string | null;
    onOpenWorker: (appKey: string) => void;
    now: number;
}

const SIDE_CAPTION: Record<NonNullable<BoardRow['side']>, string> = {
    retry: 'nieuwe poging',
    payments: 'betalingen',
};

const SIDE_HINT: Record<NonNullable<BoardRow['side']>, string> = {
    retry: 'Berichten die bij het ontvangen misliepen en het later nog eens proberen. Alleen zichtbaar zolang er iets op staat.',
    payments:
        'Betalingen uit Exact komen niet via een webhook maar via deze timer: hij vraagt Exact om afgeletterde receivables en zet een betaling klaar voor Chargebee op dezelfde queue als de rest.',
};

/** An arrow pointing the way the lane runs. */
const Arrow = ({ direction }: { direction: 'right' | 'left' }) => (
    <span className="queue-board__arrow" aria-hidden="true">
        <FontAwesomeIcon
            icon={direction === 'right' ? 'angle-right' : 'angle-left'}
        />
    </span>
);

/** Chargebee or Exact: where a lane starts or ends, the full height of the board. */
const System = ({ name }: { name: string }) => (
    <div className="queue-board__system">
        <FontAwesomeIcon icon="cloud" className="queue-board__system-icon" />
        <span className="queue-board__system-label">{name}</span>
    </div>
);

/** A queue whose messages nothing in the code reads, stated as a word rather than a gap. */
const Nobody = ({ queue }: { queue: QueueState }) => (
    <span
        className="queue-board__nobody"
        title={`Er is geen queue-trigger die aan "${queue.name}" hangt. Meestal wijst een app-setting dan naar een andere queuenaam dan waar de berichten naartoe worden geschreven.`}
    >
        <FontAwesomeIcon icon="circle-xmark" />
        niemand leest dit
    </span>
);

/**
 * Both pipelines on one board.
 *
 * Chargebee sits on the left and Exact on the right, once each, and the two lanes run between
 * them: the way to Exact on top, left to right, and the way back underneath, right to left.
 * The board scrolls sideways as a whole when it does not fit, instead of once per lane.
 *
 * Three kinds of thing, three shapes: a system is a dark block, a function app is a frame
 * with its state in the head, a queue is a white card with a count. A function is a small
 * pill inside its app.
 */
export const QueueBoard = (props: IQueueBoardProps) => {
    const poisonBySource = new Map<string, QueueState>();

    props.queues
        .filter((q) => q.stage === 'Poison' && q.poisonOf)
        .forEach((q) => poisonBySource.set(q.poisonOf as string, q));

    const workers = props.fleet?.workers ?? [];
    const { lanes, others } = buildBoard(props.queues, workers, poisonBySource);

    const queueCard = (queue: QueueState) => (
        <QueueStep
            queue={queue}
            poison={poisonBySource.get(queue.name)}
            selectedQueue={props.selectedQueue}
            onSelect={props.onSelectQueue}
            now={props.now}
        />
    );

    const node = (item: BoardNode) => {
        switch (item.kind) {
            case 'queue':
                return queueCard(item.queue);

            case 'nobody':
                return <Nobody queue={item.queue} />;

            default:
                return (
                    <FunctionChip
                        fn={item.fn}
                        findings={queueFindings(props.findings, item.fn.reads)}
                        paymentSync={props.fleet?.paymentSync}
                        onOpen={props.onOpenWorker}
                    />
                );
        }
    };

    const row = (lane: BoardLane, entry: BoardRow, index: number) => (
        <div
            key={index}
            className={`app-frame__row${
                entry.side ? ' app-frame__row--side' : ''
            }`}
        >
            {entry.side && (
                <span
                    className="app-frame__caption"
                    title={SIDE_HINT[entry.side]}
                >
                    <FontAwesomeIcon
                        icon={entry.side === 'retry' ? 'rotate-left' : 'clock'}
                    />
                    {SIDE_CAPTION[entry.side]}
                </span>
            )}

            {entry.nodes.map((item, at) => (
                <Fragment key={at}>
                    {at > 0 && <Arrow direction={lane.direction} />}
                    {node(item)}
                </Fragment>
            ))}
        </div>
    );

    const laneView = (lane: BoardLane) => (
        <div
            key={lane.pipeline}
            className={`queue-lane queue-lane--${lane.direction}`}
        >
            <h4 className="queue-lane__title">
                {lane.direction === 'left' && (
                    <FontAwesomeIcon icon="arrow-left" />
                )}
                {pipelineTitle(lane.pipeline)}
                {lane.direction === 'right' && (
                    <FontAwesomeIcon icon="arrow-right" />
                )}
            </h4>

            <div className="queue-lane__track">
                <Arrow direction={lane.direction} />

                {lane.items.map((item, at) => (
                    <Fragment key={at}>
                        {at > 0 && <Arrow direction={lane.direction} />}

                        {item.kind === 'group' ? (
                            <AppFrame
                                appKey={item.group.appKey}
                                app={item.group.app}
                                findings={appFindings(
                                    props.findings,
                                    item.group.appKey
                                )}
                                open={props.openWorker === item.group.appKey}
                                onOpen={props.onOpenWorker}
                            >
                                {item.group.rows.map((entry, index) =>
                                    row(lane, entry, index)
                                )}
                            </AppFrame>
                        ) : item.kind === 'queue' ? (
                            queueCard(item.queue)
                        ) : (
                            <Nobody queue={item.queue} />
                        )}
                    </Fragment>
                ))}

                <Arrow direction={lane.direction} />
            </div>
        </div>
    );

    return (
        <>
            {props.fleet?.hostStateElsewhere && (
                <p className="queue-board__note">
                    <FontAwesomeIcon icon="circle-info" /> Of de function apps
                    draaien, is hiervandaan niet te zien: ze laten in het
                    storage account met de queues geen timer of lease achter.
                    Hun AzureWebJobsStorage wijst waarschijnlijk naar een ander
                    account. De queues hieronder kloppen wel; voor de apps zelf
                    kun je hun stream of host-logs openen.
                </p>
            )}

            <BuildLine builds={props.fleet?.builds} />

            {lanes.length > 0 && (
                <div className="queue-board">
                    <System name="Chargebee" />
                    <div className="queue-board__lanes">
                        {lanes.map(laneView)}
                    </div>
                    <System name="Exact" />
                </div>
            )}

            {lanes.length > 0 && (
                <ul className="queue-board__legend" aria-label="Legenda">
                    <li>
                        <span className="queue-board__swatch queue-board__swatch--system" />
                        systeem
                    </li>
                    <li>
                        <span className="queue-board__swatch queue-board__swatch--app" />
                        function app
                    </li>
                    <li>
                        <span className="queue-board__swatch queue-board__swatch--function" />
                        <span>
                            functie (<FontAwesomeIcon icon="globe" /> http,{' '}
                            <FontAwesomeIcon icon="list-ul" /> queue,{' '}
                            <FontAwesomeIcon icon="clock" /> timer)
                        </span>
                    </li>
                    <li>
                        <span className="queue-board__swatch queue-board__swatch--queue" />
                        queue
                    </li>
                </ul>
            )}

            {others.length > 0 && (
                <div className="queue-board__others">
                    <h4 className="queue-lane__title">Overige queues</h4>
                    <div className="queue-board__others-list">
                        {others.map((queue) =>
                            queue.stage === 'Poison' ? (
                                <button
                                    key={queue.name}
                                    type="button"
                                    className={`queue-step__poison${
                                        props.selectedQueue === queue.name
                                            ? ' queue-step__poison--selected'
                                            : ''
                                    }`}
                                    onClick={() =>
                                        props.onSelectQueue(queue.name)
                                    }
                                >
                                    {queue.name}: {formatCount(queue.depth)}{' '}
                                    mislukt
                                </button>
                            ) : (
                                <Fragment key={queue.name}>
                                    {queueCard(queue)}
                                </Fragment>
                            )
                        )}
                    </div>
                </div>
            )}
        </>
    );
};
